import { tryParseDate } from '@shared/functions/try-parse-date';
import { BcmDepartment, BcmDepartmentDto } from '@shared/models/bcm-department';
import { BcmWorkingTimeModel, BcmWorkingTimeModelDto } from '@shared/models/bcm-working-time-model';
import {
    BcmEmployeeWorkingTimeModelAssignment,
    BcmEmployeeWorkingTimeModelAssignmentDto,
    findAssignmentForMonth,
} from '@shared/models/bcm-employee-working-time-model-assignment';
import { IFile } from '@shared/interfaces/file';
import {
    WorkTimeEmployeeTileSummary,
    WorkTimeEmployeeTileSummaryDto,
} from '@shared/models/bcm-work-time-employee-tile';

export type EmployeePosition = 'department_head' | 'deputy_head' | 'employee';

export interface BcmEmployeeProfileDto {
    salutation?: string | null;
    title?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    email2?: string | null;
    phone?: string | null;
    mobilePhone?: string | null;
    street?: string | null;
    postcode?: string | null;
    city?: string | null;
    country?: string | null;
    birthDate?: string | Date | null;
    image?: IFile | null;
}

export interface BcmEmployeeAvailableUserDto {
    user_id: string;
    profile?: {
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
    } | null;
}

export interface BcmEmployeeLicenseUsageDto {
    used: number;
    quota: number | null;
    active: boolean;
}

export interface BcmEmployeeDto {
    id?: number;
    user_id?: string;
    employee_number?: string;
    primary_department_id?: number | null;
    position?: EmployeePosition | null;
    entry_date?: string | null;
    exit_date?: string | null;
    workingTimeModelAssignments?: BcmEmployeeWorkingTimeModelAssignmentDto[];
    internal_hourly_rate?: number | null;
    annual_vacation_days?: number | null;
    prior_year_vacation_days?: number | null;
    vacation_days_taken?: number;
    vacation_days_remaining?: number | null;
    prior_year_vacation_days_remaining?: number | null;
    current_year_vacation_days_remaining?: number | null;
    /** Set by the API when HR fields were removed for this viewer (#7298). */
    compensation_redacted?: boolean;
    vacation_redacted?: boolean;
    business_email?: string | null;
    business_mobile_phone?: string | null;
    business_landline_phone?: string | null;
    isActive?: boolean;
    gps_capture_enabled?: boolean;
    primaryDepartment?: BcmDepartmentDto | null;
    workingTimeModel?: BcmWorkingTimeModelDto | null;
    additionalDepartments?: BcmDepartmentDto[];
    profile?: BcmEmployeeProfileDto | null;
    work_time_tile?: WorkTimeEmployeeTileSummaryDto;
    insertedOn?: string;
    insertedBy?: string;
    lastUpdateOn?: string | null;
    lastUpdateBy?: string | null;
}

export class BcmEmployeeAvailableUser {
    userId: string;
    profile: BcmEmployeeProfileDto | null;

    constructor(dto: BcmEmployeeAvailableUserDto = {} as BcmEmployeeAvailableUserDto) {
        this.userId = dto.user_id;
        this.profile = dto.profile ?? null;
    }

    get displayName(): string {
        const parts = [this.profile?.firstName, this.profile?.lastName].filter(Boolean);
        return parts.join(' ') || this.profile?.email || this.userId;
    }
}

export class BcmEmployee {
    id?: number;
    userId: string;
    employeeNumber: string;
    primaryDepartmentId: number | null;
    position: EmployeePosition | null;
    entryDate: Date | null;
    exitDate: Date | null;
    workingTimeModelAssignments: BcmEmployeeWorkingTimeModelAssignment[];
    internalHourlyRate: number | null;
    annualVacationDays: number | null;
    priorYearVacationDays: number;
    vacationDaysTaken: number;
    vacationDaysRemaining: number | null;
    priorYearVacationDaysRemaining: number | null;
    currentYearVacationDaysRemaining: number | null;
    compensationRedacted: boolean;
    vacationRedacted: boolean;
    businessEmail: string | null;
    businessMobilePhone: string | null;
    businessLandlinePhone: string | null;
    gpsCaptureEnabled: boolean;
    isActive: boolean;
    primaryDepartment: BcmDepartment | null;
    additionalDepartments: BcmDepartment[];
    profile: BcmEmployeeProfileDto | null;
    profileImage: IFile | null;
    workTimeTile: WorkTimeEmployeeTileSummary | null;
    insertedOn: Date | null;
    insertedBy: string;
    lastUpdateOn: Date | null;
    lastUpdateBy: string | null;

    constructor(dto: BcmEmployeeDto = {} as BcmEmployeeDto) {
        this.id = dto.id;
        this.userId = dto.user_id;
        this.employeeNumber = dto.employee_number;
        this.primaryDepartmentId = dto.primary_department_id ?? null;
        this.position = dto.position ?? null;
        this.entryDate = tryParseDate(dto.entry_date);
        this.exitDate = tryParseDate(dto.exit_date);
        this.workingTimeModelAssignments = (dto.workingTimeModelAssignments ?? [])
            .map((assignment) => new BcmEmployeeWorkingTimeModelAssignment(assignment));
        this.internalHourlyRate = dto.internal_hourly_rate != null ? Number(dto.internal_hourly_rate) : null;
        this.annualVacationDays = dto.annual_vacation_days != null ? Number(dto.annual_vacation_days) : null;
        this.priorYearVacationDays = dto.prior_year_vacation_days != null ? Number(dto.prior_year_vacation_days) : 0;
        this.vacationDaysTaken = dto.vacation_days_taken != null ? Number(dto.vacation_days_taken) : 0;
        this.vacationDaysRemaining = dto.vacation_days_remaining != null
            ? Number(dto.vacation_days_remaining)
            : null;
        this.priorYearVacationDaysRemaining = dto.prior_year_vacation_days_remaining != null
            ? Number(dto.prior_year_vacation_days_remaining)
            : null;
        this.currentYearVacationDaysRemaining = dto.current_year_vacation_days_remaining != null
            ? Number(dto.current_year_vacation_days_remaining)
            : null;
        this.compensationRedacted = !!dto.compensation_redacted;
        this.vacationRedacted = !!dto.vacation_redacted;
        this.businessEmail = dto.business_email ?? null;
        this.businessMobilePhone = dto.business_mobile_phone ?? null;
        this.businessLandlinePhone = dto.business_landline_phone ?? null;
        this.gpsCaptureEnabled = !!dto.gps_capture_enabled;
        this.isActive = dto.isActive ?? true;
        this.primaryDepartment = dto.primaryDepartment ? new BcmDepartment(dto.primaryDepartment) : null;
        this.additionalDepartments = (dto.additionalDepartments ?? []).map(department => new BcmDepartment(department));
        this.profile = dto.profile ?? null;
        this.profileImage = dto.profile?.image ?? null;
        this.workTimeTile = dto.work_time_tile
            ? new WorkTimeEmployeeTileSummary(dto.work_time_tile)
            : null;
        this.insertedOn = tryParseDate(dto.insertedOn);
        this.insertedBy = dto.insertedBy;
        this.lastUpdateOn = tryParseDate(dto.lastUpdateOn);
        this.lastUpdateBy = dto.lastUpdateBy ?? null;
    }

    get displayName(): string {
        const parts = [this.profile?.firstName, this.profile?.lastName].filter(Boolean);
        return parts.join(' ') || this.profile?.email || '';
    }

    get profileSummary(): string {
        const lines = [this.displayName, this.employeeNumber].filter(Boolean);
        return lines.join('\n');
    }

    get contactSummary(): string {
        const lines = [
            this.businessEmail,
            this.businessMobilePhone || this.businessLandlinePhone,
        ].filter(Boolean);
        return lines.join('\n');
    }

    get profileContactPhoneSummary(): string {
        const lines = [this.profile?.phone, this.profile?.mobilePhone].filter(Boolean);
        return lines.join(' / ');
    }

    get profileAddressSummary(): string {
        const profile = this.profile;
        if (!profile) {
            return '';
        }

        const streetLine = profile.street ?? '';
        const cityLine = [profile.postcode, profile.city].filter(Boolean).join(' ');
        return [streetLine, cityLine].filter(Boolean).join(', ');
    }

    get additionalDepartmentsSummary(): string {
        return this.additionalDepartments
            .map(department => department.name)
            .filter(Boolean)
            .join(', ');
    }

    get departmentSummary(): string {
        return this.primaryDepartment?.name ?? '';
    }

    get workingTimeModel(): BcmWorkingTimeModel | null {
        const today = new Date();
        return this.getWorkingTimeModelForMonth(today.getFullYear(), today.getMonth() + 1);
    }

    getWorkingTimeModelForMonth(year: number, month: number): BcmWorkingTimeModel | null {
        return findAssignmentForMonth(this.workingTimeModelAssignments, year, month)?.workingTimeModel ?? null;
    }

    get workingTimeModelSummary(): string {
        return this.workingTimeModel?.summaryLabel ?? '';
    }

    get vacationDaysTakenSummary(): string {
        return String(this.vacationDaysTaken);
    }

    get vacationDaysRemainingSummary(): string {
        return this.vacationDaysRemaining != null ? String(this.vacationDaysRemaining) : '–';
    }

    get priorYearVacationDaysSummary(): string {
        return String(this.priorYearVacationDays ?? 0);
    }

    get priorYearVacationDaysRemainingSummary(): string {
        return this.priorYearVacationDaysRemaining != null
            ? String(this.priorYearVacationDaysRemaining)
            : '–';
    }

    get currentYearVacationDaysRemainingSummary(): string {
        return this.currentYearVacationDaysRemaining != null
            ? String(this.currentYearVacationDaysRemaining)
            : '–';
    }

    toPayload(): Record<string, unknown> {
        return {
            user_id: this.userId,
            employee_number: this.employeeNumber,
            primary_department_id: this.primaryDepartmentId,
            position: this.position,
            entry_date: this.entryDate,
            exit_date: this.exitDate,
            workingTimeModelAssignments: this.workingTimeModelAssignments.map((assignment) => assignment.toPayload()),
            internal_hourly_rate: this.internalHourlyRate,
            annual_vacation_days: this.annualVacationDays,
            business_email: this.businessEmail,
            business_mobile_phone: this.businessMobilePhone,
            business_landline_phone: this.businessLandlinePhone,
            gps_capture_enabled: this.gpsCaptureEnabled,
            additionalDepartmentIds: this.additionalDepartments.map(department => department.id),
        };
    }

    toString(): string {
        return this.displayName || this.employeeNumber || '';
    }
}
